import React, { useState } from 'react';
import { LoanOfficerMetrics, FunnelStep } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { ChevronDown, ChevronUp } from 'lucide-react';
interface LoanOfficerTableProps {
  metrics: LoanOfficerMetrics[];
  onRowClick: (officerId: string) => void;
}
type SortField = 'name' | 'Created' | 'Converted' | 'App Out' | 'App Completed' | 'Submitted to UW' | 'Closing Scheduled' | 'Funded' | 'conversion' | 'cleanCount' | 'cleanPct';
type SortDirection = 'asc' | 'desc';
export function LoanOfficerTable({
  metrics,
  onRowClick
}: LoanOfficerTableProps) {
  const [sortField, setSortField] = useState<SortField>('Funded');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedMetrics = [...metrics].sort((a, b) => {
    let valA: any;
    let valB: any;
    if (sortField === 'name') {
      valA = a.officer.name;
      valB = b.officer.name;
    } else if (sortField === 'conversion') {
      valA = a.conversion.createdToFunded;
      valB = b.conversion.createdToFunded;
    } else if (sortField === 'cleanCount') {
      valA = a.cleanFiles.count;
      valB = b.cleanFiles.count;
    } else if (sortField === 'cleanPct') {
      valA = a.cleanFiles.percentage;
      valB = b.cleanFiles.percentage;
    } else {
      valA = a.counts[sortField as FunnelStep];
      valB = b.counts[sortField as FunnelStep];
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <span className="w-4 h-4 inline-block" data-id="element-82" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5" data-id="element-83" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-84" />;
  };
  const SortableHeader = ({
    field,
    children,
    className = ''
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => <th className={`px-6 py-4 font-medium text-[#706E6B] cursor-pointer hover:bg-[#F5F5F5] transition-colors select-none ${className}`} onClick={() => handleSort(field)} data-id="element-85">
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`} data-id="element-86">
        {children}
        <SortIcon field={field} data-id="element-87" />
      </div>
    </th>;
  return <Card className="p-0 overflow-hidden" noPadding data-id="element-88">
      <CardHeader bordered className="!mb-0" data-id="element-89">
        <CardTitle data-id="element-90">Performance by Loan Officer</CardTitle>
      </CardHeader>

      <div className="overflow-x-auto" data-id="element-91">
        <table className="w-full text-sm" data-id="element-92">
          <thead className="text-xs uppercase bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-93">
            <tr data-id="element-94">
              <SortableHeader field="name" data-id="element-95">Officer</SortableHeader>
              <SortableHeader field="Created" className="text-right" data-id="element-96">
                Claimed
              </SortableHeader>
              <SortableHeader field="Converted" className="text-right" data-id="element-97">
                Converted
              </SortableHeader>
              <SortableHeader field="App Out" className="text-right" data-id="element-98">
                App Out
              </SortableHeader>
              <SortableHeader field="App Completed" className="text-right" data-id="element-99">
                App Comp.
              </SortableHeader>
              <SortableHeader field="Submitted to UW" className="text-right" data-id="element-100">
                Sub. to UW
              </SortableHeader>
              <SortableHeader field="Closing Scheduled" className="text-right" data-id="element-101">
                CTC
              </SortableHeader>
              <SortableHeader field="Funded" className="text-right" data-id="element-102">
                Funded
              </SortableHeader>
              <SortableHeader field="conversion" className="text-right" data-id="element-103">
                Pull-through
              </SortableHeader>
              <SortableHeader field="cleanCount" className="text-right" data-id="element-104">
                Clean Files
              </SortableHeader>
              <SortableHeader field="cleanPct" className="text-right" data-id="element-105">
                % Clean
              </SortableHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-106">
            {sortedMetrics.length === 0 ? <tr data-id="element-107">
                <td colSpan={11} className="px-6 py-12 text-center text-[#706E6B]" data-id="element-108">
                  No data available.
                </td>
              </tr> : sortedMetrics.map(row => <tr key={row.officer.id} className="bg-white hover:bg-[#F9F9F9] transition-colors cursor-pointer group" onClick={() => onRowClick(row.officer.id)} data-id="element-109">
                  <td className="px-6 py-4" data-id="element-110">
                    <span className="font-medium text-[#181818] group-hover:underline transition-colors" data-id="element-111">
                      {row.officer.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-112">
                    {row.counts['Created'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-113">
                    {row.counts['Converted'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-114">
                    {row.counts['App Out'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-115">
                    {row.counts['App Completed'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-116">
                    {row.counts['Submitted to UW'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-117">
                    {row.counts['Closing Scheduled'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums font-medium text-[#181818]" data-id="element-118">
                    {row.counts['Funded'].toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-119">
                    {row.conversion.createdToFunded.toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums text-[#706E6B]" data-id="element-120">
                    {row.cleanFiles.count} / {row.cleanFiles.total}
                  </td>
                  <td className="px-6 py-4 text-right tabular-nums" data-id="element-121">
                    <span className={row.cleanFiles.percentage >= 70 ? 'text-[#04844B] font-medium' : row.cleanFiles.percentage >= 50 ? 'text-[#706E6B]' : 'text-[#EA001E] font-medium'} data-id="element-122">
                      {row.cleanFiles.percentage.toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </Card>;
}